import axios from 'axios'

const PRINTER_API_BASE = 'http://localhost:9000/api'
const V3_MIN_VERSION = 320
/**
 * First build that renders a `Custom` block on a KITCHEN order item, i.e. can
 * print toppings/comments one per line. Below it the kitchen slip must keep the
 * single comma-joined `toppingList` / `comments` strings — an older renderer
 * ignores the Custom block entirely, so splitting the lines out there would
 * print an item with no toppings and no comment at all.
 */
const ITEM_CUSTOM_TEXT_MIN_VERSION = 335

const state = { value: 0, loaded: false, inflight: null as Promise<void> | null }


export function parsePrinterVersion(raw: unknown): number {
  const text = String(raw ?? '').replace(/"/g, '').trim()
  if (!text) return 0

  if (!text.includes('.')) return parseInt(text, 10) || 0

  const [major = 0, minor = 0, patch = 0] = text
    .split('.')
    .map((part) => parseInt(part, 10) || 0)

  return major * 100 + Math.min(minor, 9) * 10 + Math.min(patch, 9)
}

export function detectPrinterVersion(): Promise<void> {
  if (state.loaded) return Promise.resolve()
  if (state.inflight) return state.inflight
  state.inflight = (async () => {
    try {
      const res = await axios.get(`${PRINTER_API_BASE}/setting/AppVersion`, { timeout: 3000 })
      const raw = typeof res.data === 'string' ? res.data : String(res.data)
      state.value = parsePrinterVersion(raw)
    } catch {
      state.value = 0
    } finally {
      state.loaded = true
      state.inflight = null
    }
  })()
  return state.inflight
}

export function isPrinterV3(): boolean {
  return state.value >= V3_MIN_VERSION
}

/**
 * The detected printer-service AppVersion as a number ("331" -> 331), or 0 when
 * detection has not run or failed (printer offline / old build with no
 * /setting/AppVersion route). 0 resolves to the legacy strategy, which is the
 * safe default: a legacy payload carries the numeric totals a V5 renderer
 * ignores, whereas a V3 payload on a legacy renderer prints no totals at all.
 */
export function getPrinterAppVersion(): number {
  return state.value
}

/**
 * True when the local print service can render a per-item `Custom` block on a
 * kitchen slip (3.3.4+). 0 — detection not run, printer offline, or a build with
 * no /setting/AppVersion route — resolves to false, the legacy strategy.
 */
export function supportsItemCustomText(): boolean {
  return state.value >= ITEM_CUSTOM_TEXT_MIN_VERSION
}

export { V3_MIN_VERSION, ITEM_CUSTOM_TEXT_MIN_VERSION }
